/**
 * RoutineChatEmptyState — the zero-message state for the RoutineBuilder chat pane (P12 §6.5).
 *
 * Built on the shared {@link Empty} primitive (icon tile + title + description + content), with
 * starter-suggestion pills styled to match the main chat welcome screen. Clicking a pill sends that
 * starter, which kicks off the build (RoutineBuilder grounds it and fills the editor live).
 *
 * The chips are Haiku-generated and grounded in THIS workspace's warehouses + dashboards + saved
 * context (`routineChat.recommendations`). While that loads — or if it returns nothing / errors — we
 * fall back to the static starters so the empty state never blocks or renders chip-less.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, MessageSquareText } from 'lucide-react';
import { useEffect, useRef } from 'react';

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';

import { ROUTINE_STARTER_PROMPTS } from '../lib/routine-prompts';

interface RoutineChatEmptyStateProps {
  /** Disable the starter chips until the session is ready. */
  disabled: boolean;
  onSend: (content: string) => void;
}

export function RoutineChatEmptyState({ disabled, onSend }: RoutineChatEmptyStateProps): React.ReactElement {
  const orpc = useOrpc();
  const { data } = useQuery({
    ...orpc.routineChat.recommendations.queryOptions({}),
    // Haiku powers ONLY the initial recommendation set — generate once per session and never refetch
    // (the empty state disappears once a conversation starts, so it is never regenerated post-turn).
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false, // best-effort — fall back to static starters silently
  });

  const generated = Boolean(data?.recommendations?.length);
  const chips = generated && data ? data.recommendations : ROUTINE_STARTER_PROMPTS;

  // Once per set, when it is clickable. The fixed starters often show first and the generated set
  // replaces them, so each set gets its own event and the clicks always match the last one. Keyed by
  // which set it is, not its length: a generated set of the same length is still a new set.
  const shownSets = useRef(new Set<string>());
  const setKey = generated ? 'generated' : 'fixed';
  useEffect(() => {
    if (disabled || shownSets.current.has(setKey)) return;
    shownSets.current.add(setKey);
    trackClientEvent(ANALYTICS_EVENTS.chatSuggestionShown, {
      surface: 'routine_starter',
      suggestionCount: chips.length,
    });
  }, [disabled, setKey, chips.length]);

  return (
    <Empty className="flex-1">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <MessageSquareText />
        </EmptyMedia>
        <EmptyTitle>Describe the routine you want</EmptyTitle>
        <EmptyDescription>
          A warehouse query on a schedule that posts a Slack message — optionally gated by a condition.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {/* While the chat session is being created the chips can't send yet — show a clear "getting
            ready" cue (NP-H) rather than inert-looking pills. */}
        {disabled ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <Loader2 className="size-3.5 animate-spin" />
            Getting ready…
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-2">
            {chips.map((p, i) => (
              <button
                key={p.label}
                onClick={() => {
                  trackClientEvent(ANALYTICS_EVENTS.chatSuggestionClicked, {
                    surface: 'routine_starter',
                    position: i + 1,
                    suggestionCount: chips.length,
                  });
                  onSend(p.prompt);
                }}
                className="flex h-7 items-center rounded-full border border-border bg-card px-3 text-sm text-foreground focus-ring transition-colors hover:border-primary hover:bg-muted"
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </EmptyContent>
    </Empty>
  );
}
